import React, { useState } from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { WhatsAppMessageBox } from './whatsapp';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useFunnelChannelSessions } from 'src/modules/shared/communications/hooks/api/funnels/funnel-channel-sessions';
import { TagDropdown } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { SiWhatsapp } from 'react-icons/si';
import { useAddressSelection } from 'src/modules/communications/hooks/message-box/whatsapp/address/selection';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { useWhatsAppComposerDraft } from 'src/modules/communications/hooks/message-box/whatsapp/use-composer-draft';
import { WhatsAppExpirationAlert } from './whatsapp/expiration-alert';
import type { ConversationMessageBoxProps } from 'src/modules/communications/types/whatsapp/conversation-message-box.types';
import { getWhatsAppFunnelAddresses } from 'src/modules/communications/utils/whatsapp/address.util';

const getWhatsappEmptyStateTitle = ({
  funnelAddressCount,
  hasAvailabilityError,
  contactAddressCount,
  selectedToAddressId,
  selectedFromAddressId,
  t,
}: {
  funnelAddressCount: number;
  hasAvailabilityError: boolean;
  contactAddressCount: number;
  selectedToAddressId?: number | null;
  selectedFromAddressId?: number | null;
  t: (key: TranslationKey) => string;
}): string | null => {
  if (funnelAddressCount === 0) {
    return t(
      hasAvailabilityError
        ? 'communications.messageBox.whatsapp.availabilityError'
        : 'communications.messageBox.whatsapp.emptyState.noAvailableSender'
    );
  }

  if (contactAddressCount === 0) {
    return t('communications.messageBox.whatsapp.emptyState.addContactAddress');
  }

  const needsToAddressSelection =
    contactAddressCount > 1 && !selectedToAddressId;
  const needsFromAddressSelection =
    funnelAddressCount > 1 && !selectedFromAddressId;

  if (needsToAddressSelection || needsFromAddressSelection) {
    return t('communications.messageBox.whatsapp.emptyState.selectAddresses');
  }

  return null;
};

export const ConversationMessageBox: React.FC<ConversationMessageBoxProps> = ({
  heightBoundary,
  inlinePanelContainerRef,
  conversationId,
  contactAddresses,
  funnelAddresses,
  isLoading,
  isTestConversation = false,
}) => {
  const { t } = useI18n();
  const [isReplyingAsContact, setIsReplyingAsContact] = useState(false);
  const {
    messageContent,
    changeMessageContent,
    isSendingMessage,
    startMessageSend,
  } = useWhatsAppComposerDraft();

  const funnelChannelSessionsQuery = createQuery()
    .equals('funnelConversationId', conversationId)
    .orderByDesc('createdAt')
    .build();
  const { channelSessions, loading: isLoadingChannelSessions } =
    useFunnelChannelSessions(funnelChannelSessionsQuery);

  const {
    availabilityLoading,
    availabilityError,
    whatsappContactAddresses,
    whatsappFunnelAddresses,
    selectedToAddressId,
    setSelectedToAddressId,
    selectedToAddress,
    toAddressDropdownOptions,
    selectedFromAddressId,
    setSelectedFromAddressId,
    selectedFromAddress,
    fromAddressDropdownOptions,
    correspondingSession,
  } = useAddressSelection({
    contactAddresses,
    funnelAddresses,
    channelSessions,
    isTestConversation,
    isLoading: isLoading || isLoadingChannelSessions,
  });

  // Loading may change the send context, but must not replace the user's editor.
  const contextLoading =
    isLoading ||
    isLoadingChannelSessions ||
    (availabilityLoading && (!selectedFromAddress || !selectedToAddress));

  const selectedFromAddressValue = selectedFromAddress?.address;
  const selectedToAddressValue = selectedToAddress?.address;
  const loadingFromAddress =
    contextLoading &&
    !selectedFromAddress &&
    (isLoading || getWhatsAppFunnelAddresses(funnelAddresses).length > 1);
  const loadingToAddress =
    contextLoading &&
    !selectedToAddress &&
    (isLoading || whatsappContactAddresses.length > 1);
  const addressContainerClassName =
    whatsappFunnelAddresses.length > 1 && whatsappContactAddresses.length > 1
      ? 'min-w-0 max-w-[50%]'
      : 'min-w-0 max-w-full';
  const whatsappEmptyStateTitle = contextLoading
    ? null
    : getWhatsappEmptyStateTitle({
        funnelAddressCount: whatsappFunnelAddresses.length,
        hasAvailabilityError: Boolean(availabilityError),
        contactAddressCount: whatsappContactAddresses.length,
        selectedToAddressId,
        selectedFromAddressId,
        t,
      });

  const showContextRow =
    !whatsappEmptyStateTitle ||
    whatsappFunnelAddresses.length > 1 ||
    whatsappContactAddresses.length > 1;

  return (
    <div className="border-t border-gray-200">
      <Panel
        shadow="flat"
        variant="neutral"
        className="rounded-none border-x-0 shadow-none"
      >
        <div className="flex min-w-0 flex-col gap-1 px-3 pt-1 pb-1 md:pt-3 md:pb-3 [@media(pointer:coarse)]:pt-1 [@media(pointer:coarse)]:pb-1 [&:has([data-emoji-board])]:pb-0 [@container(max-height:280px)]:gap-0">
          {showContextRow && (
            <div className="flex min-h-[2rem] min-w-0 items-center [@container(max-height:280px)]:min-h-[1.5rem]">
              {loadingFromAddress && (
                <div
                  aria-hidden="true"
                  className="flex h-8 min-w-0 w-36 max-w-[50%] items-center px-3 [@container(max-height:280px)]:h-6"
                >
                  <TextSkeleton variant="neutral" />
                </div>
              )}
              {!loadingFromAddress && whatsappFunnelAddresses.length > 1 && (
                <div className={addressContainerClassName}>
                  <TagDropdown
                    className="max-w-full [@container(max-height:280px)]:!h-6"
                    content={
                      <Text as="span" size="tiny" className="truncate">
                        {!selectedFromAddressValue
                          ? t('communications.messageBox.whatsapp.fromLabel')
                          : `${t('communications.messageBox.whatsapp.compactFromLabel')}: ${selectedFromAddressValue}`}
                      </Text>
                    }
                    variant="secondary"
                    size="xs"
                    appearance="ghost"
                    mobileTitle={t(
                      'communications.messageBox.whatsapp.fromLabel'
                    )}
                    options={fromAddressDropdownOptions}
                    onOptionSelect={(option) =>
                      setSelectedFromAddressId(Number(option.id))
                    }
                  />
                </div>
              )}

              {loadingToAddress && (
                <div
                  aria-hidden="true"
                  className="flex h-8 min-w-0 w-36 max-w-[50%] items-center px-3 [@container(max-height:280px)]:h-6"
                >
                  <TextSkeleton variant="neutral" />
                </div>
              )}
              {!loadingToAddress && whatsappContactAddresses.length > 1 && (
                <div className={addressContainerClassName}>
                  <TagDropdown
                    className="max-w-full [@container(max-height:280px)]:!h-6"
                    content={
                      <Text as="span" size="tiny" className="truncate">
                        {!selectedToAddressValue
                          ? t('communications.messageBox.whatsapp.toLabel')
                          : `${t('communications.messageBox.whatsapp.toLabel')}: ${selectedToAddressValue}`}
                      </Text>
                    }
                    variant="secondary"
                    size="xs"
                    appearance="ghost"
                    mobileTitle={t(
                      'communications.messageBox.whatsapp.toLabel'
                    )}
                    options={toAddressDropdownOptions}
                    onOptionSelect={(option) =>
                      setSelectedToAddressId(Number(option.id))
                    }
                  />
                </div>
              )}
              {(contextLoading ||
                (selectedFromAddress && selectedToAddress)) && (
                <div className="ml-auto shrink-0">
                  <WhatsAppExpirationAlert
                    channelSession={correspondingSession ?? undefined}
                    loading={contextLoading}
                  />
                </div>
              )}
            </div>
          )}

          {(contextLoading ||
            (whatsappFunnelAddresses.length > 0 &&
              whatsappContactAddresses.length > 0)) && (
            <WhatsAppMessageBox
              heightBoundary={heightBoundary}
              inlinePanelContainerRef={inlinePanelContainerRef}
              messageContent={messageContent}
              onMessageContentChange={changeMessageContent}
              isSendingMessage={isSendingMessage}
              onMessageSendStart={startMessageSend}
              toAddress={selectedToAddress?.address}
              fromAddress={selectedFromAddress?.address}
              toAddressId={selectedToAddress?.id}
              fromAddressId={selectedFromAddress?.id}
              channelSession={correspondingSession ?? undefined}
              contactId={selectedToAddress?.contactId}
              funnelConversationId={conversationId}
              funnelId={selectedFromAddress?.funnelId}
              loading={contextLoading}
              isTestConversation={isTestConversation}
              isReplyingAsContact={isReplyingAsContact}
              onReplyingAsContactChange={setIsReplyingAsContact}
            />
          )}

          {whatsappEmptyStateTitle ? (
            <EmptyState
              className={
                !showContextRow
                  ? 'min-h-[5rem] [@container(max-height:280px)]:min-h-[4.25rem]'
                  : undefined
              }
              title={whatsappEmptyStateTitle}
              icon={<SiWhatsapp className="w-full h-full" />}
              placement="horizontal"
              size="sm"
            />
          ) : null}
        </div>
      </Panel>
    </div>
  );
};
